import { useGlobalStore } from '@old/global-store/use-store';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import type {
  SilobagListResponse,
  SilobagListServiceContract,
} from '@siloreal:shared:api/service/silobag/silobag-list';
import {
  listSilobagSummaryService,
  type SilobagListSummaryResponse,
  type SilobagListSummaryServiceContract,
} from '@siloreal:shared:api/service/silobag/silobag-list-summary';
import { useSiloRealService } from '@siloreal:shared:hooks';
import type { QueryKey } from '@tanstack/react-query';
import {
  useSilobagListLogicState,
  type SilobagListLogicState,
  type SilobagTab,
  type SilosProTab,
} from '../hook/use-silobag-list-logic-state';

type Context = ReturnType<typeof useSilobagListInternalContext>;
const Context = createContext<Context | null>(null);

export function SilobagListContextProvider({ children }: PropsWithChildren) {
  const value = useSilobagListInternalContext();
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useSilobagListContext() {
  const ctx = useContext(Context);
  if (!ctx) throw new Error('useSilobagListContext must be used within SilobagListProvider');
  return ctx;
}

export type {
  SilobagListLogicState,
  SilobagListResponse,
  SilobagListSummaryResponse,
  SilobagTab,
  SilosProTab,
  Context as SilobagListContext,
};

// --- [CONTEXT] INTERNAL LOGIC STATE --- //

type SummaryRequest = {
  contract: SilobagListSummaryServiceContract;
  requestId: number;
};

type SummaryLoadingPhase = 'idle' | 'list' | 'summary';

function toSummaryContract(listContract: SilobagListServiceContract): SilobagListSummaryServiceContract {
  return {
    search: listContract.search,
    filter: listContract.filter,
  };
}

function useSilobagListInternalContext() {
  const { state, change } = useSilobagListLogicState();
  const organizationId = useGlobalStore((store) => store.organizationId);
  const isArchived = state.view === 'silobag' && state.tab === 'archived';
  const [silobagListResponse, setSilobagListResponse] = useState<SilobagListResponse | null>(null);
  const [summaryRequest, setSummaryRequest] = useState<SummaryRequest | null>(null);
  const [summaryLoadingPhase, setSummaryLoadingPhase] = useState<SummaryLoadingPhase>(() =>
    isArchived ? 'idle' : 'list'
  );

  const count = useRef(0);
  useEffect(() => {
    count.current++;
    if (count.current > 1) setSilobagListResponse(null);
  }, [state, organizationId]);

  useEffect(() => {
    if (isArchived) setSummaryLoadingPhase('idle');
  }, [isArchived]);

  const summaryQuery = useSiloRealService.query<
    SilobagListSummaryResponse,
    Error,
    SilobagListSummaryResponse,
    QueryKey
  >(() => listSilobagSummaryService(summaryRequest!.contract), {
    queryKey: ['silobag-list-summary', summaryRequest],
    enabled: summaryRequest !== null,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const startSummaryLoading = useCallback(() => {
    setSummaryLoadingPhase('list');
  }, []);

  const stopSummaryLoading = useCallback(() => {
    setSummaryLoadingPhase('idle');
  }, []);

  const triggerSummary = useCallback((listContract: SilobagListServiceContract) => {
    setSummaryLoadingPhase('summary');
    setSummaryRequest((current) => ({
      contract: toSummaryContract(listContract),
      requestId: (current?.requestId ?? 0) + 1,
    }));
  }, []);

  useEffect(() => {
    if (summaryLoadingPhase !== 'summary' || summaryQuery.isFetching) return;
    if (summaryQuery.isSuccess || summaryQuery.isError) setSummaryLoadingPhase('idle');
  }, [
    summaryLoadingPhase,
    summaryQuery.isFetching,
    summaryQuery.isSuccess,
    summaryQuery.isError,
  ]);

  const isSummaryLoading = summaryLoadingPhase !== 'idle';

  return {
    state: {
      current: state,
      change,
    },
    organization: {
      current: organizationId,
    },
    async: {
      list: {
        current: silobagListResponse,
        update: setSilobagListResponse,
      },
      summary: {
        current: !isSummaryLoading && summaryRequest ? (summaryQuery.data ?? null) : null,
        trigger: triggerSummary,
        loading: {
          current: isSummaryLoading,
          start: startSummaryLoading,
          stop: stopSummaryLoading,
        },
      },
    },
  };
}
